import Axios from "axios";
import { officeExtentions } from "../../../src/taskpane/config/constants";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import authModule from "../../../utilities/AuthModule";
import { logger } from "../../../utilities/logger";
import { tryDecodeUriComponent, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { IAuthAccount } from "../../../src/taskpane/interfaces/IAuthTokenResultDto";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { parseDocument } from "htmlparser2";
import { getElementsByTagName } from "domutils";

const extractSelectOptions = (data: any) => {
    const options = getElementsByTagName("option", parseDocument(data));

    if (Array.isArray(options)) {
        return options.map((el) => {
            const option: FilterItem = {
                title: el.children[0]['data'],
                value: el.attribs.value,
                selected: false
            }
            return option;
        });
    }

    return [];
}

const extractListItems = (data: any) => {
    const list = getElementsByTagName("li", parseDocument(data)).splice(1);
    const options: FilterItem[] = [];

    if (Array.isArray(list)) {
        list.forEach((el, index) => {
            if (index % 5 === 0) {
                options.push({
                    title: el.children[0]['data'],
                    value: el.children[0]['data'],
                    selected: false
                })
            }
        });
    }

    return options;
}

export const resetFilterValues = (
    filterSections: FilterSection[]
): FilterSection[] => {
    return filterSections.map(section => {
        return {
            ...section,
            showMoreExpanded: false,
            daysOffset: null,
            items: section.items.map(item => {
                return {
                    ...item,
                    selected: false
                }
            })
        }
    });
}

export const setFilterSectionsFromFields = async (
    isInOneDrive: boolean,
    oneDriveRootDetails: any,
    currentDirectory: IPageSettings,
    userAccount: IAuthAccount,
    filterFieldList: any[],
    serverRelPath: string,
    localizedMeText: string,
    selectedView?: IViewsDropdown
): Promise<FilterSection[]> => {
    try {
        const siteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
        const config = {
            headers: {
                Authorization: `Bearer ${isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken()}`,
            }
        }

        const filterSections = await Promise.all(
            filterFieldList
                ?.filter(x => x.RealFieldName !== 'FileSizeDisplay'
                    && x.RealFieldName !== 'Title'
                    && x.RealFieldName !== '_UIVersionString'
                    && x.Filterable !== 'FALSE'
                    && x.ShowInFiltersPane !== 'FALSE'
                    && x.GroupField !== 'TRUE'
                )
                ?.map(async field => {
                    try {
                        const data = (await Axios.post(`${siteUrl}/_api/web/lists('${currentDirectory?.sharePointListId}')/RenderListFilterData?&FieldInternalName=${field.RealFieldName}&RootFolder=${tryEncodeUriComponent(tryDecodeUriComponent(serverRelPath))}&ExcludeFieldFilteringHtml=true${selectedView?.Id ? `&ViewId=${selectedView.Id}` : ""}`, {}, config)).data;

                        const section: FilterSection = {
                            filterField: field,
                            items: data.includes('<SELECT') ? extractSelectOptions(data) : extractListItems(data),
                            showMoreExpanded: false,
                            hasSlider: field.Type === 'DateTime' ? true : false,
                            showSlider: field.Type === 'DateTime' ? true : false
                        }

                        let compactItems: FilterItem[] = [];
                        section.items?.forEach(item => {
                            if (item.title !== '(All)' && item.title !== '(Alles)') {
                                if (field.RealFieldName === 'DocIcon') {
                                    if (officeExtentions.word.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'docx')) {
                                            compactItems.push({
                                                title: 'Word',
                                                value: officeExtentions.word.values,
                                                fileType: 'docx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.excel.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'xlsx')) {
                                            compactItems.push({
                                                title: 'Excel',
                                                value: officeExtentions.excel.values,
                                                fileType: 'xlsx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.powerPoint.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'pptx')) {
                                            compactItems.push({
                                                title: 'PowerPoint',
                                                value: officeExtentions.powerPoint.values,
                                                fileType: 'pptx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.visio.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'vsdx')) {
                                            compactItems.push({
                                                title: 'Visio',
                                                value: officeExtentions.visio.values,
                                                fileType: 'vsdx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (item.title !== `(Empty)` && item.title !== `(Leeg)`) {
                                        compactItems.push({ ...item, fileType: item.value });
                                    }
                                }
                                else if (field.FieldType === "User" || field.FieldType === "UserMulti") {
                                    if (item.title === userAccount.displayName) {
                                        compactItems.push({ ...item, title: `@${localizedMeText}` });
                                    }
                                    else {
                                        compactItems.push({ ...item });
                                    }
                                }
                                else {
                                    compactItems.push({ ...item });
                                }
                            }
                        });

                        // try {
                        //   const dataSourceField = dataSourceHelper.getInputDataSource(datasourceItems, field.DisplayName);
                        //   if (dataSourceField) {
                        //     const dropdownBinder = new dataSourceDropdownBinder(dataSourceService, dataSourceField, field.DisplayName);
                        //     const property = dropdownBinder.getDataSourcePropertyToBindTo();
                        //     if (property) {
                        //       const datasourceData = await dropdownBinder.getDatasourceEntries();
                        //       const datasourceValues = dropdownBinder.bindDataSourceToDropdown(datasourceData, property);

                        //       datasourceValues.forEach(datasourceValue => {
                        //         const datasourceFilterValue: string = (datasourceValue.key as string)?.trim();
                        //         if (datasourceFilterValue) {
                        //           const compactItem = compactItems.find(x => x.value?.trim().toUpperCase() === datasourceFilterValue.toUpperCase());
                        //           if (!compactItem) {
                        //             const newFilterItem: FilterItem = {
                        //               title: datasourceFilterValue,
                        //               value: datasourceFilterValue,
                        //               selected: false
                        //             }

                        //             compactItems.push(newFilterItem);
                        //           }
                        //         }
                        //       });
                        //     }
                        //   }
                        // } catch (error) {
                        //   logger(error, "CMFileFilterPanel.tsx (Add datasource options)");
                        // }

                        if (field.FieldType === "DateTime") {
                            compactItems.sort((a, b) => {
                                try {
                                    // Convert the value of a and b to Date objects
                                    const aDate = new Date(a.value);
                                    const bDate = new Date(b.value);

                                    // Check if both a and b are valid dates
                                    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                                        // If both are valid dates, compare their timestamps to sort them
                                        return aDate.getTime() - bDate.getTime();
                                    }
                                } catch (error) {
                                    // Ignore
                                }

                                // If either a or b (or both) are not valid dates, don't change their order
                                return 0;
                            });
                        } else {
                            compactItems = compactItems
                                .sort((a, b) => (a.title?.toUpperCase() > b.title?.toUpperCase()) ? 1 : -1)
                                .map((item) => {
                                    return {
                                        ...item
                                    }
                                });
                        }

                        // section.items = [...compactItems];
                        section.items = compactItems?.filter((obj, index, self) => index === self.findIndex((o) => o.title === obj.title));

                        return section;
                    } catch (error) {
                        logger(error, "FileFilterHelper.ts (filterFields)");
                        return null;
                    }
                }));

        let displayFilterSections: FilterSection[] = [];

        (filterSections as FilterSection[])
            ?.filter(section => section)
            ?.forEach(section => {
                if (!(section.filterField.RealFieldName === 'ID' && section.filterField.FromBaseType === 'TRUE')) {
                    if (section.items?.length === 1) {
                        // Hide section if only (Empty)
                        if (section.items[0].title !== "(Empty)" && section.items[0].title !== "(Leeg)") {
                            section.showSlider = false;
                            section.hasSlider = false;
                            displayFilterSections.push(section);
                        }
                    }
                    else if (section.items?.length === 2) {
                        // Hide section slider if only 1 item
                        if (section.items[0].title === "(Empty)" || section.items[0].title === "(Leeg)") {
                            section.showSlider = false;
                            section.hasSlider = false;
                        }
                        displayFilterSections.push(section);
                    }
                    else {
                        displayFilterSections.push(section);
                    }
                }
            });

        const forceToFrontFieldName: string = "ContentType";
        const forceToFrontIndex: number = displayFilterSections.findIndex(x => x?.filterField?.RealFieldName === forceToFrontFieldName);
        if (forceToFrontIndex !== -1) {
            displayFilterSections.unshift(displayFilterSections.splice(forceToFrontIndex, 1)[0]);
        }
        return displayFilterSections.filter(section => section?.items?.length > 1);
    } catch (error) {
        logger(error, "FileFilterHelper.ts (setFilterSectionsFromFields)");
    }

    return [];
}